import { useEffect, useRef } from "react";

interface SpiralCanvasProps {
  active: boolean;
  onComplete: () => void;
  loopCount?: number;
}

// ループ回数に応じたスパイラル設定を返す
function getSpiralConfig(loopCount: number) {
  // 0回目: 初回（穏やか・虹色）
  if (loopCount <= 1) {
    return {
      duration: 5200,
      arms: 14,
      hueMode: "rainbow" as const,
      hueSpeed: 0.14,
      hueRange: 360,
      glitchFreq: 0,       // グリッチなし
      glitchStrength: 0,
      lineWidth: 0.6,
      trailAlpha: 0.055,
      flashColor: "rainbow" as const,
      flashIntensity: 0.15,
      aberration: 0.08,
      messages: [] as string[],
      centerGlowColor: "rainbow" as const,
      particleHueShift: 60,
      ringCount: 18,
      tunnelCount: 22,
      rotationMult: 18,
      finalFlash: "white" as const,
    };
  }
  // 1-2回目: 少し速く・金色が混じる
  if (loopCount <= 2) {
    return {
      duration: 5600,
      arms: 12,
      hueMode: "golden" as const,
      hueSpeed: 0.12,
      hueRange: 80,        // 金〜オレンジ域
      glitchFreq: 0,
      glitchStrength: 0,
      lineWidth: 0.7,
      trailAlpha: 0.058,
      flashColor: "golden" as const,
      flashIntensity: 0.18,
      aberration: 0.1,
      messages: ["また、戻ってきた"] as string[],
      centerGlowColor: "golden" as const,
      particleHueShift: 40,
      ringCount: 16,
      tunnelCount: 20,
      rotationMult: 19,
      finalFlash: "white" as const,
    };
  }
  // 3回目: 不穏・赤みが増す
  if (loopCount <= 3) {
    return {
      duration: 6200,
      arms: 10,
      hueMode: "red-orange" as const,
      hueSpeed: 0.09,
      hueRange: 50,
      glitchFreq: 45,
      glitchStrength: 6,
      lineWidth: 0.8,
      trailAlpha: 0.052,
      flashColor: "red" as const,
      flashIntensity: 0.22,
      aberration: 0.15,
      messages: ["まだ試みるのか", "扉は閉じている"] as string[],
      centerGlowColor: "red" as const,
      particleHueShift: 20,
      ringCount: 13,
      tunnelCount: 16,
      rotationMult: 20,
      finalFlash: "red" as const,
    };
  }
  // 4回目: 深紅・グリッチ激化
  if (loopCount <= 4) {
    return {
      duration: 6800,
      arms: 8,
      hueMode: "crimson" as const,
      hueSpeed: 0.07,
      hueRange: 30,
      glitchFreq: 25,
      glitchStrength: 14,
      lineWidth: 1.0,
      trailAlpha: 0.045,
      flashColor: "red" as const,
      flashIntensity: 0.3,
      aberration: 0.22,
      messages: ["引き返せ", "お前には無理だ", "扉は拒んでいる"] as string[],
      centerGlowColor: "red" as const,
      particleHueShift: 10,
      ringCount: 10,
      tunnelCount: 12,
      rotationMult: 21,
      finalFlash: "red" as const,
    };
  }
  // 5回目: 崩壊・奈落の予兆
  if (loopCount <= 5) {
    return {
      duration: 7400,
      arms: 6,
      hueMode: "abyss" as const,
      hueSpeed: 0.05,
      hueRange: 20,
      glitchFreq: 14,
      glitchStrength: 22,
      lineWidth: 1.2,
      trailAlpha: 0.038,
      flashColor: "red" as const,
      flashIntensity: 0.38,
      aberration: 0.3,
      messages: ["もう遅い", "ここから出られない", "お前はもう内側にいる"] as string[],
      centerGlowColor: "red" as const,
      particleHueShift: 5,
      ringCount: 8,
      tunnelCount: 9,
      rotationMult: 22,
      finalFlash: "red" as const,
    };
  }
  // 6-9回目: 奈落の底・完全崩壊
  if (loopCount <= 9) {
    return {
      duration: 8000,
      arms: 5,
      hueMode: "void" as const,
      hueSpeed: 0.04,
      hueRange: 15,
      glitchFreq: 8,
      glitchStrength: 32,
      lineWidth: 1.4,
      trailAlpha: 0.03,
      flashColor: "red" as const,
      flashIntensity: 0.45,
      aberration: 0.4,
      messages: ["逃げ場はない", "お前は選ばれた", "奈落は、お前を待っていた", "もう出口はない"] as string[],
      centerGlowColor: "red" as const,
      particleHueShift: 3,
      ringCount: 6,
      tunnelCount: 7,
      rotationMult: 24,
      finalFlash: "red" as const,
    };
  }
  // 10回目以上: 神話的・超越
  return {
    duration: 9000,
    arms: 4,
    hueMode: "transcend" as const,
    hueSpeed: 0.03,
    hueRange: 10,
    glitchFreq: 5,
    glitchStrength: 45,
    lineWidth: 1.8,
    trailAlpha: 0.022,
    flashColor: "red" as const,
    flashIntensity: 0.55,
    aberration: 0.55,
    messages: [
      "お前は神話になった",
      `${loopCount}回の螺旋`,
      "もはや人間ではない",
      "宇宙がお前を認めた",
    ] as string[],
    centerGlowColor: "red" as const,
    particleHueShift: 2,
    ringCount: 5,
    tunnelCount: 5,
    rotationMult: 26,
    finalFlash: "red" as const,
  };
}

function getHue(mode: string, elapsed: number, speed: number, range: number, armIndex: number, armCount: number): number {
  switch (mode) {
    case "rainbow":
      return (elapsed * speed + (armIndex / armCount) * 360) % 360;
    case "golden":
      return 30 + ((elapsed * speed + armIndex * (range / armCount)) % range);
    case "red-orange":
      return (elapsed * speed + armIndex * (range / armCount)) % range;
    case "crimson":
      return (elapsed * speed) % range;
    case "abyss":
    case "void":
    case "transcend":
      return (elapsed * speed) % range;
    default:
      return (elapsed * speed) % 360;
  }
}

function getCenterGlowColors(mode: string, progress: number, hueBase: number): [string, string] {
  switch (mode) {
    case "rainbow":
      return [
        `hsla(${hueBase},100%,80%,${0.5 * progress})`,
        `hsla(${(hueBase + 60) % 360},90%,60%,${0.2 * progress})`,
      ];
    case "golden":
      return [
        `hsla(42,100%,75%,${0.55 * progress})`,
        `hsla(30,90%,55%,${0.25 * progress})`,
      ];
    case "red-orange":
      return [
        `hsla(10,100%,65%,${0.6 * progress})`,
        `hsla(25,90%,45%,${0.3 * progress})`,
      ];
    case "crimson":
      return [
        `hsla(0,100%,60%,${0.7 * progress})`,
        `hsla(350,100%,35%,${0.4 * progress})`,
      ];
    case "abyss":
      return [
        `hsla(0,100%,50%,${0.8 * progress})`,
        `hsla(345,100%,25%,${0.55 * progress})`,
      ];
    case "void":
    case "transcend":
      return [
        `hsla(0,100%,45%,${0.9 * progress})`,
        `hsla(340,100%,20%,${0.65 * progress})`,
      ];
    default:
      return [
        `hsla(${hueBase},100%,70%,${0.5 * progress})`,
        `hsla(${hueBase},80%,50%,${0.2 * progress})`,
      ];
  }
}

export default function SpiralCanvas({ active, onComplete, loopCount = 1 }: SpiralCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    const cfg = getSpiralConfig(loopCount);
    const { duration, arms, hueMode, hueSpeed, hueRange, glitchFreq, glitchStrength,
      lineWidth, trailAlpha, flashIntensity, aberration, messages,
      ringCount, tunnelCount, rotationMult, finalFlash, particleHueShift } = cfg;

    const startTime = performance.now();
    completedRef.current = false;

    interface Particle {
      x: number; y: number;
      vx: number; vy: number;
      life: number; maxLife: number;
      size: number; hue: number; hueShift: number;
    }
    const particles: Particle[] = [];

    function spawnParticle(progress: number, hueBase: number) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 3 * progress;
      const spread = 80 + progress * 160;
      particles.push({
        x: cx + (Math.random() - 0.5) * spread,
        y: cy + (Math.random() - 0.5) * spread,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0,
        maxLife: 40 + Math.random() * 70,
        size: 0.8 + Math.random() * 2.5,
        hue: hueBase + Math.random() * particleHueShift,
        hueShift: 0.5 + Math.random() * 1.5,
      });
    }

    let glitchOffsetX = 0;
    let glitchOffsetY = 0;
    let glitchTimer = 0;

    function easeInOutCubic(t: number) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function draw(now: number) {
      const elapsed = now - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);
      const progress = easeInOutCubic(rawProgress);
      const speedMult = rawProgress < 0.15 ? 3.5 : 1.0 + (1 - rawProgress) * 0.5;

      const hueBase = getHue(hueMode, elapsed, hueSpeed, hueRange, 0, arms);

      // グリッチ
      if (glitchFreq > 0) {
        glitchTimer++;
        if (glitchTimer % glitchFreq === 0) {
          glitchOffsetX = (Math.random() - 0.5) * glitchStrength * 2;
          glitchOffsetY = (Math.random() - 0.5) * glitchStrength;
        } else if (glitchTimer % 4 === 0) {
          glitchOffsetX *= 0.25;
          glitchOffsetY *= 0.25;
        }
      }

      // 残像
      const effectiveTrail = hueMode === "void" || hueMode === "transcend"
        ? trailAlpha * 0.55
        : trailAlpha;
      ctx.fillStyle = `rgba(6,5,10,${effectiveTrail})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const rotation = progress * Math.PI * rotationMult * speedMult;

      // ── SPIRAL ARMS ──
      const POINTS = 220;
      const maxR = Math.max(canvas.width, canvas.height) * 0.85;

      for (let a = 0; a < arms; a++) {
        const armAngleOffset = (a / arms) * Math.PI * 2;
        const armHue = getHue(hueMode, elapsed, hueSpeed, hueRange, a, arms);
        const visibleFrac = Math.min(rawProgress * 2.5, 1);

        const gx = glitchOffsetX * (a % 3 === 0 ? 1 : 0);
        const gy = glitchOffsetY * (a % 3 === 0 ? 1 : 0);

        ctx.save();
        ctx.translate(gx, gy);
        ctx.beginPath();
        let first = true;
        for (let p = 0; p < Math.floor(POINTS * visibleFrac); p++) {
          const t = p / POINTS;
          const angle = armAngleOffset + t * Math.PI * 14 + rotation;
          const r = t * maxR * (0.05 + progress * 0.95);
          const px = cx + Math.cos(angle) * r;
          const py = cy + Math.sin(angle) * r;
          if (first) { ctx.moveTo(px, py); first = false; }
          else ctx.lineTo(px, py);
        }

        const isDeepMode = hueMode === "abyss" || hueMode === "void" || hueMode === "transcend";
        const alpha = isDeepMode ? 0.75 - rawProgress * 0.1 : 0.55 - rawProgress * 0.12;
        const sat = hueMode === "rainbow" || hueMode === "golden" ? 88 : 100;
        const light = hueMode === "rainbow" ? 65 : hueMode === "golden" ? 68 : 48;

        ctx.strokeStyle = `hsla(${armHue},${sat}%,${light}%,${alpha})`;
        ctx.lineWidth = lineWidth;
        ctx.stroke();
        ctx.restore();

        // Secondary arm
        ctx.save();
        ctx.translate(-glitchOffsetX * 0.4, -glitchOffsetY * 0.4);
        ctx.beginPath();
        first = true;
        for (let p = 0; p < Math.floor(POINTS * visibleFrac * 0.85); p++) {
          const t = p / POINTS;
          const angle = armAngleOffset + 0.15 + t * Math.PI * 14 + rotation * 1.08;
          const r = t * maxR * 0.6 * (0.05 + progress * 0.95);
          const px = cx + Math.cos(angle) * r;
          const py = cy + Math.sin(angle) * r;
          if (first) { ctx.moveTo(px, py); first = false; }
          else ctx.lineTo(px, py);
        }
        ctx.strokeStyle = `hsla(${(armHue + 30) % 360},80%,70%,${alpha * 0.35})`;
        ctx.lineWidth = lineWidth * 0.5;
        ctx.stroke();
        ctx.restore();
      }

      // ── CONCENTRIC RINGS ──
      for (let r = 0; r < ringCount; r++) {
        const ringT = (r / ringCount + rawProgress * 0.6) % 1;
        const radius = Math.max(0, (1 - ringT) * Math.min(cx, cy) * 2.0 * progress);
        const ringAlpha = ringT * (1 - ringT) * 4 * 0.28;
        const ringHue = getHue(hueMode, elapsed, hueSpeed, hueRange, r, ringCount);

        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(${ringHue},85%,60%,${ringAlpha})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      // ── TUNNEL ──
      for (let t = 0; t < tunnelCount; t++) {
        const phase_t = (t / tunnelCount + rawProgress * 1.4) % 1;
        const size = (1 - phase_t) * Math.max(canvas.width, canvas.height) * 1.1;
        const tunnelHue = getHue(hueMode, elapsed, hueSpeed, hueRange, t, tunnelCount);
        const tunnelAlpha = phase_t * (1 - phase_t) * 4 * 0.22 * progress;
        const twistAngle = rotation * 0.08 + t * 0.15;

        ctx.save();
        ctx.translate(cx + glitchOffsetX * 0.25, cy + glitchOffsetY * 0.25);
        ctx.rotate(twistAngle);
        ctx.strokeStyle = `hsla(${tunnelHue},80%,60%,${tunnelAlpha})`;
        ctx.lineWidth = 0.4 + phase_t * 0.5;
        ctx.strokeRect(-size / 2, -size / 2, size, size);
        ctx.restore();
      }

      // ── CENTRAL GLOW ──
      const glowR = 15 + progress * 200;
      const glowGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
      const [gc1, gc2] = getCenterGlowColors(hueMode, progress, hueBase);
      glowGrad.addColorStop(0, gc1);
      glowGrad.addColorStop(0.3, gc2);
      glowGrad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = glowGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, glowR, 0, Math.PI * 2);
      ctx.fill();

      // ── PARTICLES ──
      const spawnRate = rawProgress < 0.75 ? 0.7 : 0.3;
      if (Math.random() < spawnRate) spawnParticle(progress, hueBase);
      if (Math.random() < spawnRate * 0.5) spawnParticle(progress, (hueBase + particleHueShift) % 360);

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life++;
        p.hue = (p.hue + p.hueShift) % 360;

        const dx = cx - p.x;
        const dy = cy - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const pull = 0.03 + progress * 0.12;
        p.vx += (dx / dist) * pull;
        p.vy += (dy / dist) * pull;

        const spin = 0.5 + progress * 2.5;
        p.vx += (-dy / dist) * spin * 0.025;
        p.vy += (dx / dist) * spin * 0.025;

        p.x += p.vx;
        p.y += p.vy;

        const lifeRatio = p.life / p.maxLife;
        const alpha = lifeRatio < 0.15 ? lifeRatio / 0.15 : 1 - (lifeRatio - 0.15) / 0.85;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue},90%,70%,${alpha * 0.9})`;
        ctx.fill();

        if (p.life >= p.maxLife) particles.splice(i, 1);
      }

      // ── MESSAGES / LETTERS ──
      if (hueMode === "rainbow") {
        // 初回: MAVERICK が螺旋軌道
        const letters = "MAVERICK".split("");
        if (rawProgress > 0.08 && rawProgress < 0.92) {
          const lt = (rawProgress - 0.08) / 0.84;
          letters.forEach((letter, i) => {
            const angle = (i / letters.length) * Math.PI * 2 + rotation * 0.25;
            const orbitR = 50 + Math.sin(lt * Math.PI) * 220;
            const lx = cx + Math.cos(angle) * orbitR;
            const ly = cy + Math.sin(angle) * orbitR;
            const letterAlpha = Math.sin(lt * Math.PI) * 0.7;
            const lHue = (hueBase + i * 45) % 360;
            const fontSize = 16 + lt * 22;

            ctx.save();
            ctx.translate(lx, ly);
            ctx.rotate(angle + Math.PI / 2 + rotation * 0.05);
            ctx.font = `300 ${fontSize}px 'Cormorant Garamond', serif`;
            ctx.fillStyle = `hsla(${lHue},90%,75%,${letterAlpha})`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(letter, 0, 0);
            ctx.restore();
          });
        }
      } else if (hueMode === "golden") {
        // 2回目: 金色のMAVERICK
        const letters = "MAVERICK".split("");
        if (rawProgress > 0.1 && rawProgress < 0.9) {
          const lt = (rawProgress - 0.1) / 0.8;
          letters.forEach((letter, i) => {
            const angle = (i / letters.length) * Math.PI * 2 + rotation * 0.2;
            const orbitR = 40 + Math.sin(lt * Math.PI) * 180;
            const lx = cx + Math.cos(angle) * orbitR;
            const ly = cy + Math.sin(angle) * orbitR;
            const letterAlpha = Math.sin(lt * Math.PI) * 0.65;

            ctx.save();
            ctx.translate(lx, ly);
            ctx.rotate(angle + Math.PI / 2);
            ctx.font = `300 ${14 + lt * 18}px 'Cormorant Garamond', serif`;
            ctx.fillStyle = `rgba(255,210,80,${letterAlpha})`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(letter, 0, 0);
            ctx.restore();
          });
        }
      } else if (messages.length > 0) {
        // 3回目以降: 不穏なメッセージ
        if (rawProgress > 0.12 && rawProgress < 0.92) {
          const lt = (rawProgress - 0.12) / 0.8;
          const msgIndex = Math.floor(elapsed / 1400) % messages.length;
          const msg = messages[msgIndex];
          const msgAlpha = Math.sin(lt * Math.PI) * (0.8 + (loopCount - 3) * 0.04);

          // グリッチ強度に応じた揺れ
          const shakeX = glitchStrength > 10 ? (Math.random() - 0.5) * glitchStrength * 0.6 * lt : 0;
          const shakeY = glitchStrength > 10 ? (Math.random() - 0.5) * glitchStrength * 0.3 * lt : 0;

          const fontSize = 26 + lt * 14 + Math.min(loopCount - 3, 6) * 2;

          // メインテキスト
          ctx.save();
          ctx.translate(cx + shakeX + glitchOffsetX * 2, cy + shakeY + glitchOffsetY * 2);
          ctx.font = `300 ${fontSize}px 'Cormorant Garamond', serif`;
          ctx.fillStyle = `rgba(255,${Math.max(0, 60 - loopCount * 5)},${Math.max(0, 40 - loopCount * 4)},${msgAlpha})`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(msg, 0, 0);
          ctx.restore();

          // 幽霊テキスト（4回目以降）
          if (loopCount >= 4) {
            ctx.save();
            ctx.translate(cx + 3 + glitchOffsetX * 3, cy + 2);
            ctx.font = `300 ${fontSize}px 'Cormorant Garamond', serif`;
            ctx.fillStyle = `rgba(0,${Math.min(255, 150 + loopCount * 10)},255,${msgAlpha * 0.25})`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(msg, 0, 0);
            ctx.restore();
          }

          // ループ回数表示
          const countAlpha = msgAlpha * (0.4 + Math.min(loopCount - 3, 7) * 0.06);
          ctx.save();
          ctx.translate(cx + glitchOffsetX * 0.5, cy + 65 + shakeY * 0.5);
          ctx.font = `200 ${10 + Math.min(loopCount - 3, 5)}px 'Cormorant Garamond', serif`;
          ctx.fillStyle = `rgba(200,30,30,${countAlpha})`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          const countMsg = loopCount >= 10
            ? `${loopCount}回の螺旋 — 神話の領域`
            : loopCount >= 6
            ? `${loopCount}回目 — もう出口はない`
            : `${loopCount}回目の試み`;
          ctx.fillText(countMsg, 0, 0);
          ctx.restore();

          // 10回以上: 散乱する文字
          if (loopCount >= 6 && lt > 0.25) {
            const scatterLetters = "MAVERICK".split("");
            scatterLetters.forEach((l, i) => {
              const scatterAngle = (i / scatterLetters.length) * Math.PI * 2 + elapsed * 0.0008;
              const scatterR = 100 + Math.sin(elapsed * 0.002 + i) * 50;
              const sx = cx + Math.cos(scatterAngle) * scatterR + (Math.random() - 0.5) * 18 * lt;
              const sy = cy + Math.sin(scatterAngle) * scatterR + (Math.random() - 0.5) * 18 * lt;
              ctx.save();
              ctx.translate(sx, sy);
              ctx.rotate(scatterAngle + elapsed * 0.001);
              ctx.font = `300 ${9 + lt * 7}px 'Cormorant Garamond', serif`;
              ctx.fillStyle = `rgba(255,20,20,${(lt - 0.25) * 0.55})`;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(l, 0, 0);
              ctx.restore();
            });
          }
        }
      }

      // ── CHROMATIC ABERRATION ──
      if (rawProgress > 0.3 && rawProgress < 0.85 && aberration > 0) {
        const aberT = (rawProgress - 0.3) / 0.55;
        const offset = aberT * aberration * (hueMode === "rainbow" ? 60 : 100);
        const aberAlpha = aberT * aberration * 0.5;

        ctx.globalCompositeOperation = "screen";
        ctx.fillStyle = `rgba(255,0,0,${aberAlpha})`;
        ctx.fillRect(cx - offset, 0, canvas.width / 2, canvas.height);
        ctx.fillStyle = `rgba(0,0,255,${aberAlpha})`;
        ctx.fillRect(0, 0, cx + offset, canvas.height);
        ctx.globalCompositeOperation = "source-over";
      }

      // ── STROBING FLASH ──
      if (rawProgress > 0.7) {
        const flashT = (rawProgress - 0.7) / 0.3;
        const strobe = Math.abs(Math.sin(elapsed * 0.025)) * flashT;

        if (hueMode === "rainbow") {
          const flashHue = (hueBase + elapsed * 0.5) % 360;
          ctx.fillStyle = `hsla(${flashHue},100%,70%,${strobe * flashIntensity})`;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        } else if (hueMode === "golden") {
          ctx.fillStyle = `rgba(255,200,80,${strobe * flashIntensity})`;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        } else {
          ctx.fillStyle = `rgba(255,0,0,${strobe * flashIntensity})`;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // 深い段階: 暗転フラッシュ
          if (loopCount >= 5 && Math.random() < 0.08 * flashT * (loopCount - 4)) {
            ctx.fillStyle = `rgba(0,0,0,${0.6 + Math.min(loopCount - 5, 4) * 0.08})`;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
        }

        // White core flash
        const coreFlashR = 60 + strobe * 300;
        const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreFlashR);
        coreGrad.addColorStop(0, `rgba(255,255,255,${strobe * 0.5})`);
        coreGrad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = coreGrad;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // ── FINAL FLASH ──
      if (rawProgress > 0.88) {
        const finalT = (rawProgress - 0.88) / 0.12;
        const whiteAlpha = finalT < 0.5 ? finalT * 2 : 0;
        const blackAlpha = finalT > 0.5 ? (finalT - 0.5) * 2 : 0;

        if (whiteAlpha > 0) {
          if (finalFlash === "red" && loopCount >= 4) {
            ctx.fillStyle = `rgba(180,20,20,${whiteAlpha * 0.85})`;
          } else {
            ctx.fillStyle = `rgba(255,255,255,${whiteAlpha * 0.9})`;
          }
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        if (blackAlpha > 0) {
          ctx.fillStyle = `rgba(6,5,10,${blackAlpha})`;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }

      if (rawProgress < 1) {
        animRef.current = requestAnimationFrame(draw);
      } else {
        if (!completedRef.current) {
          completedRef.current = true;
          onComplete();
        }
      }
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [active, onComplete, loopCount]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-50"
      style={{ width: "100vw", height: "100vh", display: "block" }}
    />
  );
}
